import {
	defaultDropAnimationSideEffects,
	DndContext,
	DragOverlay,
	useDraggable,
	useDroppable,
} from '@dnd-kit/core';

import React, { useCallback, useEffect, useRef,useState } from 'react';

import { Tag } from '@ekhodealer/ekho-common/rev';

import type { DragEndEvent, DragStartEvent, DropAnimation } from '@dnd-kit/core';

import './KanbanBoard.scss';

const DROP_ANIMATION_DURATION_MS = 250;

const dropAnimationConfig: DropAnimation = {
	duration: DROP_ANIMATION_DURATION_MS,
	easing: 'ease',
	sideEffects: defaultDropAnimationSideEffects({
		styles: { active: { opacity: '0' } },
	}),
};

export interface ItemMoveInfo {
	/* The item that is being moved */
	itemId: string;
	/* The column that the item is being moved to */
	toColumnId: string;
}

export interface KanbanColumnData<T = unknown> {
	/* Unique identifier for this column */
	id: string;
	/* The label text for the top of the column */
	title: string;
	/* The message to be overlayed on the column
	   when the user has dragged an item over it */
	dropMessage: string;
	/* The items that belong to this column */
	items: KanbanItemData<T>[];
	/* Optional accent color (1-7). When provided, overrides index-based cycling. */
	accent?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
	/* Total item count for this column (displayed in header). Falls back to items.length. */
	count?: number;
	/* Whether there are more items to load beyond what's currently in items[] */
	hasMoreItems?: boolean;
}

export interface KanbanItemData<T = unknown> {
	/* Unique identifier for this item */
	id: string;
	/* The column that this item belongs in */
	columnId: string;
	/* The actual data object we loaded from the server
	   (we pass this down into `KanbanCard`) */
	cardData: T;
}

export interface KanbanBoardProps<T = unknown> {
	/* The columns to put items in */
	columns: KanbanColumnData<T>[];
	/* This is the component in which to render the items */
	cardComponent: (cardData: T, isDragging?: boolean) => React.ReactNode;
	/* This is a callback to know when to call the server to update */
	onItemMove?: (moveInfo: ItemMoveInfo) => void | Promise<void>;
	/* Called when the user scrolls to the bottom of a column that has more items */
	onLoadMore?: (columnId: string) => void;
	className?: string;
}

interface DraggableItemProps<T = unknown> {
	item: KanbanItemData<T>;
	cardComponent: (cardData: T) => React.ReactNode;
	isAnimating?: boolean;
}

const DraggableItem = <T,>({ item, cardComponent, isAnimating }: DraggableItemProps<T>) => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructuring dnd-kit hook; not all return values needed
	const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
		id: item.id,
		data: item,
	});

	const style = {
		opacity: isDragging || isAnimating ? 0 : 1,
	};

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading -- dnd-kit requires spreading listeners and attributes on draggable element
		<div ref={setNodeRef} style={style} {...listeners} {...attributes} className='kanban-item'>
			{cardComponent(item.cardData)}
		</div>
	);
};

interface DroppableColumnProps<T = unknown> {
	columnData: KanbanColumnData<T>;
	cardComponent: (cardData: T) => React.ReactNode;
	accent: number;
	animatingItemId: string | null;
	onLoadMore?: (columnId: string) => void;
}

const DroppableColumn = <T,>({
	columnData,
	cardComponent,
	accent,
	animatingItemId,
	onLoadMore,
}: DroppableColumnProps<T>) => {
	const { isOver, active, setNodeRef } = useDroppable({
		id: columnData.id,
	});

	// Sentinel ref for infinite scroll — triggers onLoadMore when scrolled into view
	const sentinelRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || !columnData.hasMoreItems || !onLoadMore) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					onLoadMore(columnData.id);
				}
			},
			{ threshold: 0 },
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [columnData.hasMoreItems, columnData.id, onLoadMore]);

	// Only show drop target if the active item is over a column that isn't its source column
	const isActiveDropTarget = isOver && active && active.data?.current?.columnId !== columnData.id;

	return (
		<div
			ref={setNodeRef}
			className='kanban-column'
			data-accent={accent}
			data-active-droptarget={isActiveDropTarget}>
			<div className='kanban-column-header'>
				<Tag appearance='outline' accent={accent as 1 | 2 | 3 | 4 | 5 | 6 | 7}>
					{columnData.title}
				</Tag>
				<span className='kanban-column-count'>
					{columnData.count ?? columnData.items.length}
				</span>
			</div>
			<div className='kanban-column-items' data-drop-message={columnData.dropMessage}>
				{columnData.items.map((item) => (
					<DraggableItem
						key={item.id}
						item={item}
						cardComponent={cardComponent}
						isAnimating={item.id === animatingItemId}
					/>
				))}
				{columnData.hasMoreItems && onLoadMore && (
					<div ref={sentinelRef} className='kanban-column-sentinel' />
				)}
			</div>
		</div>
	);
};

export const KanbanBoard = <T,>({
	columns,
	cardComponent,
	onItemMove,
	onLoadMore,
	className,
}: KanbanBoardProps<T>) => {
	const [activeItem, setActiveItem] = useState<KanbanItemData<T> | null>(null);

	// Local state for optimistic updates - updated immediately on drop
	const [localColumns, setLocalColumns] = useState<KanbanColumnData<T>[]>(columns);

	// Track which item is mid-drop-animation (hidden in target column until animation completes)
	const [animatingItemId, setAnimatingItemId] = useState<string | null>(null);

	// Sync local state with props when parent updates
	useEffect(() => {
		setLocalColumns(columns);
	}, [columns]);

	const handleDragStart = useCallback((event: DragStartEvent) => {
		const { active } = event;
		const draggedItem = active.data?.current as KanbanItemData<T>;
		setActiveItem(draggedItem);
	}, []);

	const handleDragEnd = useCallback(
		async (event: DragEndEvent) => {
			const { active, over } = event;

			if (!over || !active) {
				setActiveItem(null);
				return;
			}

			const itemId = active.id as string;
			const targetColumnId = over.id as string;
			const draggedItem = active.data?.current as KanbanItemData<T>;

			// Cancel the drop if the item is being dropped in the same column it started from
			if (draggedItem?.columnId === targetColumnId) {
				setActiveItem(null);
				return;
			}

			// Check if the target is a valid column
			const targetColumn = localColumns.find((col) => col.id === targetColumnId);
			if (!targetColumn) {
				console.warn(`Target column ${targetColumnId} not found`);
				setActiveItem(null);
				return;
			}

			// Hide the card in the target column during the drop animation
			setAnimatingItemId(itemId);

			// Optimistic update: move item immediately in local state (hidden via animatingItemId)
			setLocalColumns((prevColumns) => {
				const newColumns = prevColumns.map((col) => ({ ...col, items: [...col.items] }));

				// Find and remove item from source column
				let movedItem: KanbanItemData<T> | null = null;
				for (const col of newColumns) {
					const itemIndex = col.items.findIndex((item) => item.id === itemId);
					if (itemIndex !== -1) {
						[movedItem] = col.items.splice(itemIndex, 1);
						if (col.count != null) col.count -= 1;
						break;
					}
				}

				// Add item to target column
				if (movedItem) {
					const targetCol = newColumns.find((col) => col.id === targetColumnId);
					if (targetCol) {
						// Update the item's columnId to reflect its new location
						targetCol.items.unshift({ ...movedItem, columnId: targetColumnId });
						if (targetCol.count != null) targetCol.count += 1;
					}
				}

				return newColumns;
			});

			// Notify parent to persist the change (don't await - fire and forget)
			if (onItemMove) {
				const result = onItemMove({ itemId, toColumnId: targetColumnId });
				// Handle promise rejection if onItemMove returns a promise
				if (result && typeof result.catch === 'function') {
					result.catch((error: unknown) => {
						console.error('Failed to move item:', error);
						// Parent should handle rollback by updating props, which will sync to localColumns
					});
				}
			}

			// After drop animation completes, reveal the card and clear the overlay
			setTimeout(() => {
				setAnimatingItemId(null);
				setActiveItem(null);
			}, DROP_ANIMATION_DURATION_MS);
		},
		[localColumns, onItemMove],
	);

	return (
		<div data-component='kanban-board' className={className}>
			<DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
				{localColumns.map((columnData, index) => {
					// Use column-provided accent if available, otherwise cycle from 1-7
					const accent = columnData.accent ?? (((index % 7) + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7);

					return (
						<DroppableColumn
							key={columnData.id}
							columnData={columnData}
							cardComponent={cardComponent}
							accent={accent}
							animatingItemId={animatingItemId}
							onLoadMore={onLoadMore}
						/>
					);
				})}
				<DragOverlay dropAnimation={dropAnimationConfig}>
					{activeItem ? (
						<div className='kanban-item kanban-item-drag-overlay'>
							{cardComponent(activeItem.cardData, true)}
						</div>
					) : null}
				</DragOverlay>
			</DndContext>
		</div>
	);
};
